import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useCopy } from '../../i18n';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  icon?: LucideIcon;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  /** Conteúdo extra abaixo da mensagem (ex.: campo de confirmação por digitação). */
  extra?: React.ReactNode;
  confirmDisabled?: boolean;
}

// Tokens, não paleta do Tailwind: bg-red-50/amber-50 não trocam no dark.
const variantMap: Record<string, { bg: string; text: string; btnVariant: string }> = {
  danger: { bg: 'bg-[var(--color-danger-bg)]', text: 'text-[var(--color-danger)]', btnVariant: 'danger' },
  warning: { bg: 'bg-[var(--color-warning-bg)]', text: 'text-[var(--color-warning)]', btnVariant: 'danger' },
  info: { bg: 'bg-[var(--color-primary-bg)]', text: 'text-[var(--color-primary)]', btnVariant: 'primary' },
};

const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'info',
  icon: Icon = AlertTriangle,
  onConfirm,
  onCancel,
  isLoading = false,
  extra,
  confirmDisabled = false
}: ConfirmModalProps) => {
  // O default sai do dicionário, não da assinatura: hook não roda na lista de
  // parâmetros. Quem passa rótulo próprio continua mandando nele.
  const t = useCopy().common.actions;
  const heading = title ?? t.confirm;
  const confirmText = confirmLabel ?? t.confirm;
  const cancelText = cancelLabel ?? t.cancel;
  const v = variantMap[variant] || variantMap.info;

  return (
    <AlertDialog open={isOpen} onOpenChange={(open: boolean) => { if (!open && !isLoading) onCancel(); }}>
      <AlertDialogContent className="max-w-[440px]">
        <AlertDialogHeader>
          <div className="flex items-start justify-between">
            <div className={cn("flex items-center justify-center w-11 h-11 rounded-lg", v.bg, v.text)}>
              <Icon size={24} />
            </div>
          </div>
          <AlertDialogTitle>{heading}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
          {extra}
        </AlertDialogHeader>
        <AlertDialogFooter className="border-t border-[var(--color-border)] pt-4">
          <AlertDialogCancel
            className={cn(
              buttonVariants({ variant: 'outline', size: 'md' }),
            )}
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault();
              if (!isLoading) onCancel();
            }}
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              buttonVariants({ variant: v.btnVariant as 'primary' | 'danger', size: 'md' }),
            )}
            disabled={isLoading || confirmDisabled}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
            {isLoading ? t.wait : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmModal;
